/**
 * Zod schemas for the Activation Code module.
 *
 * Covers:
 *   - ActivationPoolCreateSchema   (admin creates per-token pool)
 *   - ActivationPoolPatchSchema    (admin updates pool)
 *   - ActivationPoolListQuerySchema (pagination + is_active filter)
 *   - ActivationDistributeSchema   (admin batch-distributes to users)
 *   - ActivationRedeemSchema       (user redeems activation code)
 *   - ActivationBatchListQuerySchema (admin lists distribution batches)
 */
import { z } from 'zod';

const ACTIVATION_TOKENS = ['rp', 'tp', 'ep'] as const;

// ---- Admin: pool CRUD -------------------------------------------------------

export const ActivationPoolCreateSchema = z.object({
  token: z.enum(ACTIVATION_TOKENS),
  pool_balance: z
    .string()
    .refine((v) => Number(v) >= 0, 'pool_balance must be non-negative'),
  is_active: z.boolean().default(true),
  note: z.string().max(500).optional(),
});

export const ActivationPoolPatchSchema = z.object({
  pool_balance: z
    .string()
    .refine((v) => Number(v) >= 0, 'pool_balance must be non-negative')
    .optional(),
  is_active: z.boolean().optional(),
  note: z.string().max(500).optional(),
});

export const ActivationPoolListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  is_active: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});

// ---- Admin: batch distribute ------------------------------------------------

export const ActivationDistributeSchema = z.object({
  token: z.enum(ACTIVATION_TOKENS),
  /** Per-user amount to distribute. */
  amount: z
    .string()
    .refine((v) => Number(v) > 0, 'amount must be positive'),
  /** User IDs to receive the distribution. */
  user_ids: z
    .array(z.coerce.number().int().positive())
    .min(1, 'at least one user_id required')
    .max(1000, 'max 1000 users per batch'),
  note: z.string().max(500).optional(),
});

// ---- User: redeem -----------------------------------------------------------

export const ActivationRedeemSchema = z.object({
  /** Amount of activation_code balance to consume. */
  amount: z
    .string()
    .refine((v) => Number(v) > 0, 'amount must be positive'),
});

// ---- Admin: batch list ------------------------------------------------------

export const ActivationBatchListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  token: z.enum(ACTIVATION_TOKENS).optional(),
  admin_id: z.coerce.number().int().positive().optional(),
});

// ---- Inferred types ---------------------------------------------------------

export type ActivationPoolCreateRequest = z.infer<typeof ActivationPoolCreateSchema>;
export type ActivationPoolPatchRequest = z.infer<typeof ActivationPoolPatchSchema>;
export type ActivationPoolListQuery = z.infer<typeof ActivationPoolListQuerySchema>;
export type ActivationDistributeRequest = z.infer<typeof ActivationDistributeSchema>;
export type ActivationRedeemRequest = z.infer<typeof ActivationRedeemSchema>;
export type ActivationBatchListQuery = z.infer<typeof ActivationBatchListQuerySchema>;
